<script setup lang="ts">
import ImageCropper from '~/components/images/cropper.vue'
import HeightExpand from '~/components/transitions/heightExpand.vue'
import { BtnGhost, BtnOrange } from '~/components/ui/Buttons'

import { useGetMediaUrl } from '~/composables/useGetMediaUrl'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useUserInfluencerStore } from '~/stores/userInfluencer'

import type { UserKind } from '~/types/UserKind'

type Props = {
  pk: number
  slug: string
  userKind: UserKind
  name?: string
}
type Emits = {
  'export-picture-data-url': [dataURL: string | null]
  'influencer-profile-picture-uploaded': []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { coreFetch } = useProvideCoreFetch()
const { getMediaUrl } = useGetMediaUrl()

const { SET: SET_USER_INFLUENCER } = useUserInfluencerStore()

const innerKind = ref<null | string>(null)
const picture = ref<null | string>(null)
const error = ref(false)
const loading = ref(false)

const cmpkind = computed<string | undefined>(() =>
  props.userKind === 'influencer' && !!innerKind.value
    ? innerKind.value
    : undefined,
)
const cropper = ref<InstanceType<typeof ImageCropper> | null>(null)

onMounted(() => fetchKinds())

function fetchKinds() {
  coreFetch
    .$get('/influencer/tags/')
    .then((resp) => {
      innerKind.value = resp.identity_tags?.influencer_kind ?? null
    })
    .catch()
}

async function confirmCrop(): Promise<void> {
  if (!cropper.value) return

  picture.value = cropper.value.getCroppedCanvas() || null

  cropper.value.close()
  await nextTick()
  await uploadPicture()
}

function addPicture(): void {
  if (!cropper.value) return

  cropper.value.addPicture()
}

function handleProceedClick(): void {
  if (!picture.value) return addPicture()

  emit('export-picture-data-url', picture.value)
}

async function uploadPicture(): Promise<void> {
  if (!picture.value || !cropper.value || loading.value) return

  loading.value = true
  try {
    await cropper.value
      .upload({
        id: props.pk,
        slug: props.slug,
        kind: props.userKind,
      })
      .catch()

    if (props.userKind === 'influencer') {
      SET_USER_INFLUENCER({
        has_profile_picture: true,
      })
      emit('influencer-profile-picture-uploaded')
    }
  } finally {
    loading.value = false
  }
}

function exportPicture(): void {
  emit('export-picture-data-url', picture.value)
}
</script>

<template>
  <div class="tw-relative tw-w-full">
    <ImageCropper
      ref="cropper"
      :modal-title="$t('band.profile.slug.edit.picture01')"
    >
      <template #actions>
        <div class="bottomActionWrap">
          <BtnGhost @click="addPicture">
            {{ $t('band.profile.slug.edit.picture02') }}
          </BtnGhost>
          <BtnOrange @click="confirmCrop">
            {{ $t('band.profile.slug.edit.picture03') }}
          </BtnOrange>
        </div>
      </template>
    </ImageCropper>
    <div
      class="tw-relative tw-w-full tw-rounded tw-border-2 tw-border-solid tw-border-gray-400 tw-bg-white tw-p-6"
    >
      <div
        class="tw-absolute tw-left-[calc(50%-(107px/2))] tw-top-[calc(-107px/2)] tw-flex tw-h-[107px] tw-w-[107px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-shadow-round"
        @click="addPicture"
      >
        <div
          class="tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-gray-300"
        >
          <transition name="fade" mode="out-in">
            <img
              v-if="!picture"
              :key="0"
              class="tw-h-full tw-max-h-[56px] tw-w-full tw-max-w-[56px] tw-object-contain tw-object-center"
              :src="getMediaUrl('takePicture.svg')"
            />
            <img
              v-else
              :key="1"
              class="tw-h-full tw-w-full tw-object-cover tw-object-center"
              :src="picture"
            />
          </transition>
        </div>
        <transition name="fade" mode="out-in">
          <div v-if="picture" class="modPen">
            <i class="fa-solid fa-pen"></i>
          </div>
        </transition>
      </div>
      <div v-if="name" class="name">
        {{ name }}
      </div>
      <div class="tw-grid tw-gap-2">
        <BtnOrange class="tw-w-full" @click="handleProceedClick">
          <span v-if="!picture">
            {{ $t('shared.pictureUploadCard01') }}
          </span>
          <span v-else>
            {{ $t('common.next') }}
          </span>
        </BtnOrange>
        <HeightExpand>
          <BtnGhost v-if="!picture" @click="exportPicture">
            {{ $t('shared.pictureUploadCard02') }}
          </BtnGhost>
        </HeightExpand>
      </div>
    </div>
    <transition name="fade" mode="out-in">
      <div v-if="error" class="errorMessage">
        {{ $t('shared.pictureUploadCard03') }}
      </div>
    </transition>
  </div>
</template>

<style scoped lang="scss">
.modPen {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--color-white);
  cursor: pointer;
  box-shadow: 0 1px 2px 0 var(--color-dark-ebb-grey);
}
.modPen > i.fa-solid {
  font-size: 13px;
  color: var(--color-black);
}

.name {
  font-size: var(--font-size-title);

  color: var(--color-black);
  text-align: center;
  margin: 62px auto 4px auto;
  min-height: 36px;
}

.skip {
  text-align: center;
  margin: 16px auto 32px auto;
  color: var(--color-masala-grey-h);
  font-weight: 600;
  font-size: var(--font-size);
  cursor: pointer;
}

.errorMessage {
  color: var(--color-error);
  text-align: center;
  width: 100%;
}

.bottomActionWrap {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin: 16px;
}

.bottomActionWrap > .change {
  cursor: pointer;
  margin-right: 16px;
}
</style>
